import React, { useState } from "react";
import TeamBadge from "@/components/app/TeamBadge";
import { X, GripVertical } from "lucide-react";

/**
 * Reusable picker:
 * - Left grid of teams (clickable, big cards with crest + TLA + name)
 * - Right column of N labeled slots; clicking a team auto-fills the next empty slot
 * - Drag-reorder slots; click ✕ to remove
 */
export default function TeamPicker({
  pool,            // [{ team, label? }]
  values,          // [team_id or null]  length = slotCount
  setValues,       // (newArr) => void
  slotCount,
  slotLabels,      // ["1st","2nd","3rd","4th"] or T1..T8
  disabled = false,
  layout = "vertical", // currently only vertical
  testidPrefix = "tp",
}) {
  const [dragFrom, setDragFrom] = useState(null);

  const placedSet = new Set(values.filter(Boolean));

  const handleTeamClick = (team_id) => {
    if (disabled) return;
    // Toggle: if already placed, remove it
    const existingIdx = values.indexOf(team_id);
    if (existingIdx >= 0) {
      const next = [...values];
      next[existingIdx] = null;
      // compact (shift up to keep slots contiguous)
      const compact = next.filter(Boolean);
      while (compact.length < slotCount) compact.push(null);
      setValues(compact);
      return;
    }
    // Place in first empty slot
    const emptyIdx = values.findIndex((x) => !x);
    if (emptyIdx === -1) return; // all full
    const next = [...values];
    next[emptyIdx] = team_id;
    setValues(next);
  };

  const removeAt = (idx) => {
    const next = [...values];
    next[idx] = null;
    const compact = next.filter(Boolean);
    while (compact.length < slotCount) compact.push(null);
    setValues(compact);
  };

  const onDragStart = (idx) => () => setDragFrom(idx);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (toIdx) => (e) => {
    e.preventDefault();
    if (dragFrom == null || dragFrom === toIdx) return;
    const next = [...values];
    const [moved] = next.splice(dragFrom, 1);
    next.splice(toIdx, 0, moved);
    while (next.length < slotCount) next.push(null);
    setValues(next.slice(0, slotCount));
    setDragFrom(null);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr_280px] gap-4 md:gap-6">
      {/* LEFT: team pool */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {pool.map(({ team, label }) => {
          const isPlaced = placedSet.has(team.team_id);
          const order = values.indexOf(team.team_id);
          return (
            <button
              key={team.team_id}
              data-testid={`${testidPrefix}-team-${team.team_id}`}
              disabled={disabled}
              onClick={() => handleTeamClick(team.team_id)}
              className={`flex items-center gap-3 p-3 border transition-colors relative ${
                isPlaced
                  ? "border-primary-accent bg-primary-accent/10"
                  : "border-zinc-800 hover:border-white/30"
              }`}
            >
              {label && <span className="label-tag absolute top-1.5 right-2 text-zinc-500">{label}</span>}
              <TeamBadge team={team} size="lg"/>
              {isPlaced && (
                <span className="ml-auto w-7 h-7 bg-primary-accent text-white flex items-center justify-center font-display text-base flex-shrink-0">
                  {slotLabels[order] || order + 1}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* RIGHT: slots */}
      <div className="surface-card p-3 self-start sticky top-[80px]">
        <div className="label-tag mb-3">Your Order · Drag to Reorder</div>
        <div className="space-y-1.5">
          {Array.from({ length: slotCount }).map((_, idx) => {
            const tid = values[idx];
            const team = tid ? pool.find((p) => p.team.team_id === tid)?.team : null;
            return (
              <div
                key={tid || `empty-${idx}`}
                data-testid={`${testidPrefix}-slot-${idx}`}
                draggable={!!team && !disabled}
                onDragStart={onDragStart(idx)}
                onDragOver={onDragOver}
                onDrop={onDrop(idx)}
                className={`flex items-center gap-2 border p-2 ${
                  team
                    ? "border-primary-accent bg-black cursor-grab active:cursor-grabbing"
                    : "border-zinc-800 border-dashed text-zinc-600"
                }`}
              >
                <span className="font-display text-primary-accent w-8 text-sm">{slotLabels[idx] || idx + 1}</span>
                {team ? (
                  <>
                    <GripVertical size={12} className="text-zinc-500"/>
                    <TeamBadge team={team} size="md" className="flex-1"/>
                    <button
                      data-testid={`${testidPrefix}-remove-${idx}`}
                      onClick={() => removeAt(idx)}
                      className="w-6 h-6 flex items-center justify-center text-zinc-500 hover:text-primary-accent"
                    ><X size={12}/></button>
                  </>
                ) : (
                  <span className="text-xs uppercase tracking-widest">Empty</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
